import { DateTime } from 'luxon';

// export enum RegistrationStatus {
//   UNPAID, PAID, CANCELLED, DELETED
// }

export interface KeyLabelPair<TKey = string> {
  key: TKey;
  label: string;
}


export enum PaymentMode {
  NONE = 'N/A',
  CARD = 'Card',
  WIRE = 'Wire',
  INVOICE = 'Invoice'
}

function getPaymentModes(): KeyLabelPair<keyof typeof PaymentMode>[] {
  return Object.keys(PaymentMode).map(key => {
    return {
      key: key as keyof typeof PaymentMode,
      label: PaymentMode[key as keyof typeof PaymentMode]
    }
  });
}

export enum InvoiceStatus {
  UNPAID = 'Unpaid',
  PAID = 'Paid',
  CREDITED = 'Credited',
  PCREDITED = 'Partially credited'
}

function getInvoiceStatuses(): KeyLabelPair<keyof typeof InvoiceStatus>[] {
  return Object.keys(InvoiceStatus).map(key => {
    return {
      key: key as keyof typeof InvoiceStatus,
      label: InvoiceStatus[key as keyof typeof InvoiceStatus]
    }
  });
}

export enum DiscountType {
  PERCENT = 'Percentage',
  FIXED_AMOUNT = 'Fixed amount'
}

function getDiscountTypes(): KeyLabelPair<keyof typeof DiscountType>[] {
  return Object.keys(DiscountType).map(key => {
    return {
      key: key as keyof typeof DiscountType,
      label: DiscountType[key as keyof typeof DiscountType]
    }
  });
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .normalize('NFD')                     // split accented letters
    .replace(/[\u0300-\u036f]/g, '')      // remove diacritics
    .replace(/[^a-z0-9\s-]/g, '')         // remove special chars
    .replace(/\s+/g, '-')                 // replace spaces with hyphens
    .replace(/-+/g, '-')                  // collapse multiple hyphens
    .replace(/^-|-$/g, '');               // trim hyphens from ends
}

export interface PaymentDetails {
  registrationId: number;
  pfInvoiceNumber: string;
  paymentDate: string;
  paymentAmount: number;
  paymentCurrency: string;
  paymentMethod: string;
  paymentReference: string;
}

export interface PaymentResponse {
  status: string;
  invoiceNumber: string;
  message: string;
}

export interface InvoicingData {
  organisation: string;
  contactName: string;
  contactEmail: string;
  address1: string;
  address2: string | null;
  zipCode: string;
  city: string;
  country: string;
  noVAT: boolean;
  vatNumber: string | null;
  poNumber: string | null;
}

export interface RegistrationData {
  organisation: string;
  promoCode?: string | null;
  selectedFee?: Fee | null;
  firstAttendee: Attendee;
  additionalAttendees: Attendee[];
}

export interface Fee {
  id: number
  name: string
  target: string
  description: string
  earlyBirdPrice: number | null
  generalPrice: number
  priority?: number
}

export interface Country {
  code: string;
  enName: string;
  frName: string;
}

export interface Attendee {
  id?: number;
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  corresponding: boolean;
  confirmationSentOn?: string | null;
  preferences?: Record<number, number[]>;
}

export interface CheckoutData {
  eventShortName: string | null;
  registrationData: RegistrationData | null;
  invoicingData: InvoicingData | null;
  totalTickets: number;
  payingTickets: number;
  freeTickets: number;
  fullRateTickets: number;
  discountRateTickets: number;
  fullRate: number;
  discountRate: number;
  totalAmount: number;
  token: string | null;
  // honeypot field: must stay empty; bots tend to fill every input
  hpField?: string | null;
  // epoch millis when the form was rendered; too-fast submissions are bots
  formLoadedAt?: number | null;
  paymentMode: string | null;
  successUrl: string | null;
  cancelUrl: string | null;
  pendingUrl: string | null;
}


export interface Organisation {
  id: number;
  name: string;
  vat: string | null;
  address1: string;
  address2: string | null;
  zip: string;
  city: string;
  country: Country | null;
  email: string;
  contactName: string;
}

export interface Registration {
  id: number;
  seqNum: number;
  date: string;
  deleted: boolean;
  organisation: string;
  promoCode: PromoCode;
  poNumber: string;
  totalTickets: number;
  payingTickets: number;
  freeTickets: number;
  fullRateTickets: number;
  discountRateTickets: number;
  fullRate: number;
  discountRate: number;
  appliedDiscount: number;
  appliedDiscountType: keyof typeof DiscountType;
  totalAmount: number;
  // appliedRate: number;
  paymentMode: string;
  customer: Customer | null;
  event: Event;
  fee: Fee | null;
  attendees: Attendee[];
  invoice: Invoice | null;
}

export interface Invoice {
  id: number | null;
  number: string;
  date: string;
  type: string;
  status: string;

  registrationId: number;
  organisation: string;
  vatNumber: string;
  feeApplied: string;
  paymentMode: string;
  paymentStatus: string;
  registrationDate: string;

  customerName: string;
}

export interface Customer {
  id: number;
  name: string;
  vatNumber: string;
  address1: string;
  address2: string;
  zip: string;
  city: string;
  country: Country;
  email: string;
  contactName: string;
}

export interface RegistrationCreateRequest {
  date: string;
  organisation: string;
  eventId: number;
  firstAttendee: Attendee;
  additionalAttendees: Attendee[];
}

export interface RegistrationUpdateRequest {
  id: number;
  date: string;
  organisation: string;
  poNumber: string;
  paymentMode: string;
  customer: Customer;
  feeId: number;
  promoCodeId: number;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceStatus: string;
  attendees: Attendee[];
}

export class PromoCode {
  constructor(
    public id: number,
    public code: string,
    public description: string | null,
    // public codeType: string, // 'PERCENT' | 'AMOUNT'    
    public discountType: keyof typeof DiscountType, // 'PERCENT' | 'AMOUNT'    
    public discountValue: number,
    public validUntil: string,
    public uses: number,
    public maxUses: number,
    public active: boolean
  ) { }

  isExpired(): boolean {
    const nowBrussels = DateTime.now().setZone('Europe/Brussels');
    const expiryDateBrussels = DateTime.fromISO(this.validUntil, { zone: 'Europe/Brussels' }).endOf('day');
    return nowBrussels >= expiryDateBrussels;
  }

  isValid(): boolean {
    if (!this.active) {
      return false;
    }
    if (this.maxUses !== 0 && this.uses >= this.maxUses) {
      return false;
    }
    if (this.validUntil) {
      const expiryDateBrussels = DateTime.fromISO(this.validUntil, { zone: 'Europe/Brussels' }).endOf('day');
      const nowBrussels = DateTime.now().setZone('Europe/Brussels');

      return expiryDateBrussels >= nowBrussels;
    }
    return true;
  }
}

export interface Disclaimer {
  id?: number | null,
  title: string,
  body: string,
  priority: number
}

export interface OptionItem {
  id: number,
  priority: number,
  label: string,
  shortLabel: string,
  hint: string | null,
}

export interface OptionGroup {
  id: number,
  priority: number,
  exclusive: boolean,
  minAnswers: number,
  maxAnswers: number,
  label: string,
  shortLabel: string,
  hint: string | null,
  optionItems: OptionItem[]
}

export class User {
  constructor(
    public id: number,
    public username: string,
    public firstname: string | null,
    public lastname: string | null,
    public email: string,
    public enabled: boolean,
    public roles: string[]
  ) { }
}

export class Event {
  constructor(
    public id: number,
    public name: string,
    public shortName: string,
    public pageTitle: string,
    public headerText: Disclaimer,
    public invoicePrefix: string,
    public date: string | null,
    public endDate: string | null,
    public url: string | null,
    public location: string | null,
    public shortLocation: string | null,
    public logoFileName: string | null,
    public freeEvent: boolean,
    public allowMultipleRegistrations: boolean,
    public usesPromoCodes: boolean,
    public oneFreeFor: number,
    public vatRate: number,
    public earlyBirdDeadline: string | null,
    public registrationOpenDate: string | null,
    public registrationCloseDate: string | null,
    public fees: Fee[],
    public disclaimers: Disclaimer[],
    public paymentConditions: Disclaimer,
    public contactEmail: string,
    public hotelBookingUrl: string | null,
    public optionGroups: OptionGroup[],
  ) { };

  isRegistrationOpen() {
    const nowBrussels = DateTime.now().setZone('Europe/Brussels');

    const openDate = (this.registrationOpenDate ? DateTime.fromISO(this.registrationOpenDate, { zone: brusselsZone }).startOf('day') : null);
    const closeDate = (this.registrationCloseDate ? DateTime.fromISO(this.registrationCloseDate, { zone: brusselsZone }).endOf('day') : null);
    // console.debug('Today:', today);
    // console.debug('Event Registration Open Date:', this.registrationOpenDate);
    // console.debug('Event Registration Close Date:', this.registrationCloseDate);
    // Check if the event has registration dates defined
    // registration is open only if registrationOpenDate is undefined or in the past and registrationCloseDate is undefined or in the future
    return (!openDate || (openDate <= nowBrussels)) && (!closeDate || (closeDate >= nowBrussels));
  }

  isEarlyBirdActive() {
    const nowBrussels = DateTime.now().setZone('Europe/Brussels');
    const ebDate = (this.earlyBirdDeadline ? DateTime.fromISO(this.earlyBirdDeadline, { zone: brusselsZone }).endOf('day') : null);
    const v = (ebDate != null) && (nowBrussels <= ebDate);
    return v;
  }

  isEventInThePast() {
    const nowBrussels = DateTime.now().setZone('Europe/Brussels');
    const eventEndDate = (this.endDate ? DateTime.fromISO(this.endDate, { zone: brusselsZone }).endOf('day') : (this.date ? DateTime.fromISO(this.date, { zone: brusselsZone }).endOf('day') : null));
    return (eventEndDate != null) && (nowBrussels > eventEndDate);
  }

  isNotOpenYet() {
    const nowBrussels = DateTime.now().setZone('Europe/Brussels');
    const registrationOpenDate = (this.registrationOpenDate ? DateTime.fromISO(this.registrationOpenDate, { zone: brusselsZone }).startOf('day') :  null);
    return (registrationOpenDate != null) && (nowBrussels < registrationOpenDate);
  }
}


export type EventState = {
  event: Event | null;
  notFound: boolean;
};


export interface GenericResponse {
  status: 'success' | 'error';
  message: string;
}

// export function isBlank(str: string | null | undefined): boolean {
//   return !str || str.trim() === '';
// }


export const paymentModes = getPaymentModes();
export const invoiceStatuses = getInvoiceStatuses();
export const discountTypes = getDiscountTypes();
export const brusselsZone = 'Europe/Brussels';
export const PredefinedDiscounts = [0, 5, 10, 15, 20, 25, 50];
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'd/M/yyyy',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};


